import type { SagaIterator } from 'redux-saga';
import {
  call,
  getContext,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import type { Client } from '@core/api/utils';
import { CLIENT_CONTEXT } from '@core/api/utils';
import { isMe } from '@core/auth/redux';
import { toOverviewUrl } from '@core/links/members';
import { track } from '@engage/analytics/actions';
import { logError } from '@engage/error-reporting/logError';
import { shouldShowPrivateInfo } from '@engage/members/models';
import { loadMember } from '@engage/members/redux';
import { isMembersSuccessActionForMember } from '@engage/members/sagas';
import { getMember } from '@engage/members/selectors';
import { toWorkoutHistoryPath } from '@engage/overview/urls';
import type { RequestNextWorkoutsPageAction } from '@engage/workout-history/redux';
import { requestNextWorkoutsPage, resetWorkouts } from '@engage/workout-history/redux';
import { loadWorkoutNextHistoryPage } from '@engage/workout-history/sagas';
import { getTotalWorkouts } from '@engage/workout-history/selectors';

import { isMemberPath } from '@members/profile/urls';
import { getViewedMemberIdFromPath } from '@members/routes/workouts';
import { updateViewedMember } from '@members/viewed-member/redux';
import { trackViewedWorkoutHistoryPage } from '../analytics';
import { WorkoutHistoryPageActionTypes } from './actions';

export const loadWorkoutsForWorkoutHistory = function* (
  client: Client,
  action: any,
): SagaIterator {
  const memberId = getViewedMemberIdFromPath(action.payload.router)! as string; // We know we're on the workouts page
  const member = yield select(getMember, memberId);

  yield call(
    loadWorkoutNextHistoryPage,
    client,
    requestNextWorkoutsPage(member.id, action.payload) as RequestNextWorkoutsPageAction,
  );
};

export const loadWorkoutHistoryPageSaga = function* (
  client: Client,
  action: any,
): SagaIterator {
  try {
    const viewedMemberId = getViewedMemberIdFromPath(action.payload.router)! as string;
    yield put(updateViewedMember(viewedMemberId));
    let member = yield select(getMember, viewedMemberId);

    if (!member) {
      yield put(loadMember(viewedMemberId));
      const membersAction = yield take(isMembersSuccessActionForMember(viewedMemberId));
      member = membersAction.payload;
    }

    const isViewedMemberMe = yield select(isMe, member.id);
    if (isViewedMemberMe && isMemberPath(action.payload.router.asPath)) {
      yield put(
        action.payload.router.replace(
          toWorkoutHistoryPath(isViewedMemberMe, viewedMemberId),
        ),
      );
    }

    if (!shouldShowPrivateInfo(member)) {
      yield put(action.payload.router.replace(toOverviewUrl(member.username)));
    } else {
      yield put(resetWorkouts());
      yield call(loadWorkoutsForWorkoutHistory, client, action);
    }
    const totalWorkouts = yield select(getTotalWorkouts);
    const sharedLeaderboardTag = action.payload.sharedLeaderboardTag;
    yield put(
      track(
        trackViewedWorkoutHistoryPage(
          member,
          action.payload.router.asPath,
          totalWorkouts,
          sharedLeaderboardTag,
        ),
      ),
    );
  } catch (err) {
    logError(err, 'loadWorkoutHistoryPageSaga');
  }
};

export const workoutHistoryPageSaga = function* (): SagaIterator {
  const client = yield getContext(CLIENT_CONTEXT);
  yield takeLatest(
    WorkoutHistoryPageActionTypes.RequestNewWorkouts,
    loadWorkoutHistoryPageSaga,
    client,
  );
  yield takeEvery(
    WorkoutHistoryPageActionTypes.RequestMoreWorkouts,
    loadWorkoutsForWorkoutHistory,
    client,
  );
};
